import { skipToken } from "@reduxjs/toolkit/query";
import React, { useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  Box,
  CircularProgress,
  Typography,
  Skeleton,
  Alert,
  IconButton,
  Fade,
} from "@mui/material";
import {
  Close as CloseIcon,
  Error as ErrorIcon,
  Assignment as ActivityIcon,
} from "@mui/icons-material";
import { useGetWorkItemQuery } from "../../store/services/workItemService";
import ActivityDialog from "../activity-dialog/ActivityDialog";

const ActivityRouteDialog: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(true);
  const {
    data: activity,
    isLoading,
    error,
  } = useGetWorkItemQuery(id ? { workItemId: Number(id) } : skipToken);

  // Handle dialog close
  const handleClose = () => {
    setIsOpen(false);

    // Determine where to navigate on close
    const background = location.state?.background;
    if (background) {
      navigate(-1);
    } else {
      navigate(-1);
    }
  };

  // Loading state with skeleton dialog
  if (isLoading && !activity) {
    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        {/* Loading Header */}
        <DialogTitle
          sx={{
            background: "linear-gradient(135deg, #1976d2 0%, #1976d2dd 100%)",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            pr: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: 2,
                bgcolor: "rgba(255,255,255,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <ActivityIcon />
            </Box>
            <Box>
              <Skeleton
                variant="text"
                width={200}
                height={32}
                sx={{ bgcolor: "rgba(255,255,255,0.2)" }}
              />
              <Skeleton
                variant="text"
                width={120}
                height={20}
                sx={{ bgcolor: "rgba(255,255,255,0.1)" }}
              />
            </Box>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <Fade in={true}>
              <Box sx={{ textAlign: "center" }}>
                <CircularProgress
                  size={48}
                  sx={{
                    mb: 2,
                    color: "primary.main",
                  }}
                />
                <Typography variant="h6" color="text.secondary" gutterBottom>
                  Laddar aktivitet...
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Hämtar information från servern
                </Typography>
              </Box>
            </Fade>
          </Box>

          {/* Loading Skeleton Content */}
          <Box sx={{ mt: 3 }}>
            {/* Customer Info Skeleton */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
                mb: 3,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 2, mb: 2 }}
              >
                <Skeleton variant="circular" width={48} height={48} />
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="60%" height={24} />
                  <Skeleton variant="text" width="40%" height={16} />
                </Box>
              </Box>
              <Skeleton
                variant="rectangular"
                width="100%"
                height={80}
                sx={{ borderRadius: 1 }}
              />
            </Box>

            {/* Details Skeleton */}
            <Box
              sx={{
                p: 2,
                border: "1px solid",
                borderColor: "grey.200",
                borderRadius: 2,
              }}
            >
              <Skeleton variant="text" width="30%" height={24} sx={{ mb: 2 }} />
              <Box
                sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}
              >
                {[1, 2, 3, 4].map((i) => (
                  <Box key={i}>
                    <Skeleton variant="text" width="50%" height={16} />
                    <Skeleton variant="text" width="80%" height={20} />
                  </Box>
                ))}
              </Box>
            </Box>
          </Box>
        </DialogContent>
      </Dialog>
    );
  }

  // Error state
  if (error && !activity) {
    return (
      <Dialog
        open={isOpen}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            bgcolor: "error.main",
            color: "white",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ErrorIcon />
            <Typography variant="h6">Kunde inte ladda aktivitet</Typography>
          </Box>
          <IconButton onClick={handleClose} sx={{ color: "white" }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 3 }}>
          <Alert
            severity="error"
            sx={{
              borderRadius: 2,
              "& .MuiAlert-message": {
                width: "100%",
              },
            }}
          >
            <Typography variant="subtitle1" gutterBottom>
              Ett fel uppstod
            </Typography>
            <Typography variant="body2">
              Aktiviteten kunde inte laddas. Kontrollera din internetanslutning
              och försök igen.
            </Typography>
            <Box sx={{ mt: 2, display: "flex", gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Aktivitets-ID: {id}
              </Typography>
            </Box>
          </Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // No ID provided
  if (!id) {
    return (
      <Dialog open={isOpen} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Typography variant="h6">Ogiltigt aktivitets-ID</Typography>
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning">Inget giltigt aktivitets-ID angivet.</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  // Success state - show the actual activity dialog
  if (activity) {
    return (
      <ActivityDialog
        open={isOpen}
        onClose={handleClose}
        activity={activity}
      />
    );
  }

  // Fallback
  return null;
};

export default ActivityRouteDialog;
